import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Upload, 
  X, 
  Plus, 
  MapPin, 
  Calendar, 
  FileText,
  Save,
  Eye
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { createPost } from '../services/supabase';
import toast from 'react-hot-toast';

const CreatePost = () => {
  const navigate = useNavigate();
  const { isAuthenticated, profile } = useAuth();
  const [isPreview, setIsPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    mainImage: '',
    era: '',
    coordinates: {
      latitude: '',
      longitude: ''
    },
    chapters: [
      {
        header: '',
        body: '',
        image: ''
      }
    ]
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to create a post');
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCoordinateChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      coordinates: {
        ...prev.coordinates,
        [field]: value
      }
    }));
  };

  const handleChapterChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      chapters: prev.chapters.map((chapter, i) => 
        i === index ? { ...chapter, [field]: value } : chapter
      )
    }));
  };

  const addChapter = () => {
    setFormData(prev => ({
      ...prev,
      chapters: [...prev.chapters, { header: '', body: '', image: '' }]
    }));
  };

  const removeChapter = (index) => {
    if (formData.chapters.length > 1) {
      setFormData(prev => ({
        ...prev,
        chapters: prev.chapters.filter((_, i) => i !== index)
      }));
    }
  };

  const handleImageUpload = (e, chapterIndex = null) => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, you'd upload to a service like Cloudinary
      // For now, we'll use a placeholder URL
      const imageUrl = URL.createObjectURL(file);
      
      if (chapterIndex !== null) {
        handleChapterChange(chapterIndex, 'image', imageUrl);
      } else {
        handleInputChange('mainImage', imageUrl);
      }
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    
    if (!formData.coordinates.latitude || !formData.coordinates.longitude) {
      toast.error('Coordinates are required');
      return false;
    }
    
    const lat = parseFloat(formData.coordinates.latitude);
    const lng = parseFloat(formData.coordinates.longitude);
    
    if (isNaN(lat) || lat < -90 || lat > 90) {
      toast.error('Invalid latitude');
      return false;
    }
    
    if (isNaN(lng) || lng < -180 || lng > 180) {
      toast.error('Invalid longitude');
      return false;
    }
    
    if (formData.chapters.some(chapter => !chapter.header.trim() || !chapter.body.trim())) {
      toast.error('All chapters must have a header and body');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const postData = {
        title: formData.title,
        content: formData.chapters.map(ch => `${ch.header}\n\n${ch.body}`).join('\n\n'),
        image: formData.mainImage,
        author_id: profile?.id
      };
      
      const { data, error } = await createPost(postData);
      
      if (error) {
        throw error;
      }
      
      toast.success('Post created successfully!');
      
      // Reset form
      setFormData({
        title: '',
        mainImage: '',
        era: '',
        coordinates: { latitude: '', longitude: '' },
        chapters: [{ header: '', body: '', image: '' }]
      });
      
      navigate('/posts');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            Create Historical Monument Post
          </h1>
          <p className="text-lg text-gray-600">
            Share a historical monument with the community
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-xl shadow-lg p-8"
        >
          {/* Preview Toggle */}
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setIsPreview(!isPreview)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                isPreview 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Eye className="w-4 h-4" />
              <span>{isPreview ? 'Edit' : 'Preview'}</span>
            </button>
          </div>

          {isPreview ? (
            /* Preview Mode */
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-3xl font-serif font-bold mb-4">{formData.title || 'Untitled Monument'}</h2>
                {formData.mainImage && (
                  <img src={formData.mainImage} alt="Preview" className="w-full h-64 object-cover rounded-lg mb-4" />
                )}
                {formData.era && (
                  <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    {formData.era}
                  </span>
                )}
              </div>
              
              {formData.chapters.map((chapter, index) => (
                chapter.body && (
                  <div key={index} className="space-y-4">
                    <h3 className="text-2xl font-serif font-semibold">{chapter.header}</h3>
                    {chapter.image && (
                      <img src={chapter.image} alt={chapter.header} className="w-full h-48 object-cover rounded-lg" />
                    )}
                    <p className="text-gray-700 leading-relaxed">{chapter.body}</p>
                  </div>
                )
              ))}
            </div>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <motion.div variants={itemVariants} className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="post-title"
                    name="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter monument title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Historical Era
                  </label>
                  <input
                    type="text"
                    id="post-era"
                    name="era"
                    value={formData.era}
                    onChange={(e) => handleInputChange('era', e.target.value)}
                    placeholder="e.g., Ancient, Medieval, Modern"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline w-4 h-4 mr-1" />
                    Latitude *
                  </label>
                  <input
                    type="number"
                    id="post-latitude"
                    name="latitude"
                    step="any"
                    value={formData.coordinates.latitude}
                    onChange={(e) => handleInputChange('coordinates.latitude', e.target.value)}
                    placeholder="Enter latitude"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline w-4 h-4 mr-1" />
                    Longitude *
                  </label>
                  <input
                    type="number"
                    id="post-longitude"
                    name="longitude"
                    step="any"
                    value={formData.coordinates.longitude}
                    onChange={(e) => handleInputChange('coordinates.longitude', e.target.value)}
                    placeholder="Enter longitude"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Image
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                      <Upload className="w-5 h-5 mr-2" />
                      <span>Upload Image</span>
                      <input
                        type="file"
                        id="post-main-image"
                        name="mainImage"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e)}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {formData.mainImage && (
                    <div className="mt-2">
                      <img src={formData.mainImage} alt="Preview" className="h-32 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Chapters */}
              <motion.div variants={itemVariants} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">Story Chapters</h3>
                  <button
                    type="button"
                    onClick={addChapter}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Chapter</span>
                  </button>
                </div>

                {formData.chapters.map((chapter, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Chapter {index + 1}</h4>
                      {formData.chapters.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChapter(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chapter Header *
                      </label>
                      <input
                        type="text"
                        id={`chapter-header-${index}`}
                        name={`chapter-header-${index}`}
                        value={chapter.header}
                        onChange={(e) => handleChapterChange(index, 'header', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Chapter title"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chapter Content *
                      </label>
                      <textarea
                        id={`chapter-content-${index}`}
                        name={`chapter-content-${index}`}
                        value={chapter.body}
                        onChange={(e) => handleChapterChange(index, 'body', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Write the historical story for this chapter..."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chapter Image
                      </label>
                      <label className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                        <Upload className="w-5 h-5 mr-2" />
                        <span>Upload Image</span>
                        <input
                          type="file"
                          id={`chapter-image-${index}`}
                          name={`chapter-image-${index}`}
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, index)}
                          className="hidden"
                        />
                      </label>
                      {chapter.image && (
                        <div className="mt-2">
                          <img src={chapter.image} alt="Chapter preview" className="h-32 object-cover rounded-lg" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={itemVariants} className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/discover')}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSubmitting ? 'Submitting...' : 'Submit for Review'}</span>
                </button>
              </motion.div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CreatePost;
